import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { catchError, tap, timeout, retry } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';
import { ApiCacheStorage } from '../utils/api-cache-storage';

// Shared offline banner state for cache fallbacks
export const offlineBanner$ = new BehaviorSubject<boolean>(false);

export class ApiCache {
  static async get<T>(key: string): Promise<T | null> {
    return ApiCacheStorage.get<T>(key);
  }

  static async set<T>(key: string, operation: string, value: T, ttlMs?: number): Promise<void> {
    await ApiCacheStorage.set<T>(key, operation, value, ttlMs);
  }
}

/**
 * Shared error handler for HTTP services.
 * Logs detailed error info and returns fallback value or throws error.
 * @param operation Name of the operation (for logging)
 * @param fallback Fallback value to return (optional)
 * @returns Function to use in catchError()
 */
export function handleHttpError<T>(operation: string, fallback?: T) {
  return (err: any): Observable<T> => {
    const msg = err?.message || err?.statusText || err?.toString();
    const stack = err?.stack || '';
    console.error(`[HTTP ERROR] ${operation} failed:`, msg, err);
    if (stack) {
      console.error(`[HTTP ERROR] Stack trace for ${operation}:`, stack);
    }
    if (fallback !== undefined) {
      return of(fallback);
    }
    return throwError(() => err);
  };
}

export function withErrorHandler<T>(options: {
  operation: string;
  fallback?: T;
  cacheKey?: string;
  cache?: boolean;
  timeoutMs?: number;
  retries?: number;
}) {
  const key = ApiCacheStorage.normalizeKey(options.operation, options.cacheKey);
  const ttlMs = ApiCacheStorage.ttlForOperation(options.operation);
  const useCache = options.cache ?? false;
  const timeoutMs = options.timeoutMs ?? 30000;
  const retries = options.retries ?? 2;

  return (source: Observable<T>): Observable<T> =>
    source.pipe(
      timeout(timeoutMs),
      retry(retries),
      tap((data) => {
        if (useCache) {
          void ApiCache.set(key, options.operation, data, ttlMs);
          offlineBanner$.next(false);
        }
      }),
      catchError((err) => {
        const msg = err?.message || err?.statusText || err?.toString();
        const stack = err?.stack || '';
        console.error(`[HTTP ERROR] ${options.operation} failed:`, msg, err);
        if (stack) {
          console.error(`[HTTP ERROR] Stack trace for ${options.operation}:`, stack);
        }
        if (useCache) {
          return from(ApiCache.get<T>(key)).pipe(
            switchMap((cached) => {
              if (cached !== null) {
                offlineBanner$.next(true);
                return of(cached);
              }

              offlineBanner$.next(false);

              if (options.fallback !== undefined) {
                return of(options.fallback);
              }

              return throwError(err);
            })
          );
        }
        if (options.fallback !== undefined) {
          return of(options.fallback);
        }
        return throwError(err);
      })
    );
}
